import React, { useState } from 'react';
import { Users, BookOpen, TrendingUp, Award, RefreshCw } from 'lucide-react';
import { useDashboard } from '@/hooks/reporting/useDashboard';
import MetricsCard from '@/components/reports/cards/MetricsCard';
import LineChart from '@/components/reports/charts/LineChart';
import BarChart from '@/components/reports/charts/BarChart';
import PieChart from '@/components/reports/charts/PieChart';
import EmployeePerformanceTable from '@/components/reports/tables/EmployeePerformanceTable';
import PeriodTypeSelector from '@/components/reports/filters/PeriodTypeSelector';
import DateRangePicker from '@/components/reports/filters/DateRangePicker';
import CacheIndicator from '@/components/reports/common/CacheIndicator';
import EmptyState from '@/components/reports/common/EmptyState';
import type { PeriodType, AdminOverviewRequest } from '@/types/reporting-v2';

// Helper function to get normalized date range for cache optimization
const getNormalizedDateRange = (periodType: PeriodType) => {
  const end_date = new Date();
  // Normalize to END of current day for consistent caching
  end_date.setHours(23, 59, 59, 999);

  const start_date = new Date();
  // Normalize to START of day for consistent caching
  start_date.setHours(0, 0, 0, 0);

  switch (periodType) {
    case 'daily':
      // Already set to start of day
      break;
    case 'weekly':
      start_date.setDate(start_date.getDate() - 7);
      break;
    case 'monthly':
      start_date.setMonth(start_date.getMonth() - 1);
      break;
    case 'quarterly':
      start_date.setMonth(start_date.getMonth() - 3);
      break;
    case 'yearly':
      start_date.setFullYear(start_date.getFullYear() - 1);
      break;
    case 'custom':
      // For custom, dates will be set by date picker
      break;
  }

  return {
    start_date: start_date.toISOString(),
    end_date: end_date.toISOString()
  };
};

export default function AdminOverview() {
  // Initialize with normalized dates for cache efficiency
  const initialDates = React.useMemo(() => getNormalizedDateRange('monthly'), []);

  const [params, setParams] = useState<AdminOverviewRequest>({
    period_type: 'monthly',
    start_date: initialDates.start_date,
    end_date: initialDates.end_date,
  });

  const { data, isLoading, error, refetch } = useDashboard('admin-overview', params);

  const handlePeriodChange = (period: PeriodType) => {
    // Auto-calculate date range based on period type with normalization
    if (period !== 'custom') {
      const normalizedDates = getNormalizedDateRange(period);
      setParams({
        period_type: period,
        start_date: normalizedDates.start_date,
        end_date: normalizedDates.end_date
      });
    } else {
      // Keep current dates when switching to custom
      setParams(prev => ({ ...prev, period_type: period }));
    }
  };

  const handleDateRangeChange = (start: string, end: string) => {
    // Normalize custom dates for cache efficiency
    const start_date = new Date(start);
    start_date.setHours(0, 0, 0, 0);

    const end_date = new Date(end);
    end_date.setHours(23, 59, 59, 999);

    setParams({
      period_type: 'custom',
      start_date: start_date.toISOString(),
      end_date: end_date.toISOString()
    });
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading dashboard</h3>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Overview</h1>
          <p className="text-sm text-gray-500 mt-1">
            Comprehensive view of organization-wide learning metrics
          </p>
        </div>
        {data && (
          <CacheIndicator
            metadata={{
              cached_at: data.generated_at,
              is_fresh: !data.is_cached,
              ttl_seconds: data.expires_at ? Math.floor((new Date(data.expires_at).getTime() - new Date().getTime()) / 1000) : undefined
            }}
            onRefresh={() => refetch()}
            refreshing={isLoading}
          />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Period
            </label>
            <PeriodTypeSelector value={params.period_type} onChange={handlePeriodChange} />
          </div>

          {params.period_type === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Custom Date Range</label>
              <DateRangePicker
                startDate={params.start_date}
                endDate={params.end_date}
                onChange={handleDateRangeChange}
              />
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          {/* Skeleton for metrics cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
                  <div className="h-8 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !data ? (
        <EmptyState
          icon={Users}
          title="No data available"
          description="Unable to load dashboard data. Please try again later."
        />
      ) : (
        <>
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.widgets?.metric_cards?.map((card: any, index: number) => {
              const iconMap: Record<string, any> = {
                'users': Users,
                'user-check': Users,
                'check-circle': TrendingUp,
                'star': Award,
                'clock': BookOpen,
                'award': Award,
              };
              const Icon = iconMap[card.icon] || Users;
              const colorMap: Record<number, string> = {
                0: 'primary',
                1: 'success',
                2: 'info',
                3: 'warning',
                4: 'info',
                5: 'warning',
              };

              return (
                <MetricsCard
                  key={index}
                  title={card.title}
                  value={typeof card.value === 'string' ? card.value : card.value?.toLocaleString()}
                  change={card.change_percentage}
                  icon={Icon}
                  color={colorMap[index] || 'primary'}
                  loading={false}
                />
              );
            })}
          </div>

          {/* Completion Trend */}
          {data.widgets?.completion_trend?.labels?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Completion Trend</h2>
              <LineChart
                labels={data.widgets.completion_trend.labels}
                series={[
                  {
                    name: 'Completions',
                    data: data.widgets.completion_trend.datasets?.[0]?.data || [],
                    color: '#10b981'
                  }
                ]}
                height={300}
              />
            </div>
          )}

          {/* Department Comparison and Role Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Department Comparison */}
            {data.widgets?.department_comparison?.labels?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Department Comparison
                </h2>
                <BarChart
                  data={data.widgets.department_comparison.labels.map((label: string, index: number) => ({
                    label,
                    value: data.widgets.department_comparison.datasets?.[1]?.data?.[index] || 0,
                    color: (data.widgets.department_comparison.datasets?.[1]?.data?.[index] || 0) >= 50 ? '#10b981' : '#3b82f6',
                  }))}
                  height={300}
                />
              </div>
            )}

            {/* Role Performance */}
            {data.widgets?.role_performance?.labels?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Role Performance
                </h2>
                <BarChart
                  data={data.widgets.role_performance.labels.map((label: string, index: number) => ({
                    label,
                    value: data.widgets.role_performance.datasets?.[0]?.data?.[index] || 0,
                    color: '#8b5cf6',
                  }))}
                  height={300}
                />
              </div>
            )}
          </div>

          {/* Recent Completions */}
          {data.widgets?.recent_completions && data.widgets.recent_completions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Completions</h2>
              <div className="space-y-3">
                {data.widgets.recent_completions.map((completion: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-green-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{completion.course_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{completion.user_name}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {new Date(completion.completed_at).toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {completion.time_spent_hours.toFixed(2)}h
                        </span>
                      </div>
                    </div>
                    <span className="flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Completed
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
